import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ApproverStep,
  QApproversSection,
  QManageApprovalStepsModal,
  toApproverSteps,
  useApprovalCriteria,
  useCreateApprovalCriteria,
  useCurrentUser,
  useToastProvider,
  useUpdateApprovalCriteria,
} from '@qualio/ui-components';

import { DocumentOverviewContext } from '../../Context';
import { shouldRenderManageApproversButton } from '../../RenderLogic';

const toastProps = {
  id: 'sequential-approvers-property-toast',
  replace: true,
};

export const SequentialApprovers = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const [isManageStepsModalOpen, setIsManageStepsModalOpen] = useState(false);

  const itemId = String(qualioDocument.id);
  const { data: criteria } = useApprovalCriteria({ itemId });
  const { mutateAsync: createApprovalCriteria } = useCreateApprovalCriteria();
  const { mutateAsync: updateApprovalCriteria } = useUpdateApprovalCriteria();

  const canManageSteps = useMemo(
    () => shouldRenderManageApproversButton(qualioDocument, currentUser),
    [currentUser, qualioDocument],
  );

  const saveSteps = async (
    steps: readonly ApproverStep[],
    removeComment?: string,
  ) => {
    const savedSteps = toApproverSteps(criteria ?? null);
    try {
      if (savedSteps.length) {
        await updateApprovalCriteria({
          itemId,
          steps,
          savedSteps,
          removeComment,
        });
      } else {
        await createApprovalCriteria({ itemId, steps });
      }
      showToast({
        'data-cy': 'sequential-approvers-save-success-toast',
        ...toastProps,
        status: 'success',
        title: t('notifications.approversAssigned'),
        description: t('notifications.successfullyChangedApprovers'),
      });
    } catch {
      showToast({
        'data-cy': 'sequential-approvers-save-error-toast',
        ...toastProps,
        status: 'error',
        title: t('notifications.error'),
        description: t('errors.failedToApplyChangesToApprovers', {
          error: 'Please try again.',
        }),
      });
    }
  };

  return (
    <>
      <QApproversSection
        itemId={itemId}
        size="md"
        onManageSteps={
          canManageSteps ? () => setIsManageStepsModalOpen(true) : undefined
        }
      />
      {isManageStepsModalOpen && (
        <QManageApprovalStepsModal
          itemId={itemId}
          onSave={saveSteps}
          onClose={() => setIsManageStepsModalOpen(false)}
        />
      )}
    </>
  );
};
